import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { withIcon } from '../../../core/components/Icon.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const DotIcon = withIcon(faCircle);

export interface AssistantCardMetaLine {
    icon: React.ReactNode;
    label?: React.ReactNode;
    trailing?: React.ReactNode;
}

const STATUS_TONES = {
    warning: { dot: 'text-content-warning-weak', label: 'text-content-warning-default' },
    danger: { dot: 'text-content-danger-weak', label: 'text-content-danger-default' },
    agent: { dot: 'text-content-agent-weak', label: 'text-content-agent-default' },
};

export const AssistantCardStatus = ({ tone, label }: { tone: keyof typeof STATUS_TONES; label: React.ReactNode }) => (
    <span className="flex flex-none items-center gap-1">
        <DotIcon className={classnames('text-[8px]', STATUS_TONES[tone].dot)} />
        <span className={classnames('text-xs font-medium', STATUS_TONES[tone].label)}>{label}</span>
    </span>
);

/** The hub card frame of the Automation Hub design: tile icon, title and subtitle, an optional header addon such as
 *  a status or a menu, brand chips and icon meta lines. The whole card links to its detail page (`to`), or acts as a
 *  button (`onClick`) for things without one, such as email automations. Assistants, Worki projects and automations
 *  share it so the hub stays one surface. */
const AssistantCardShell = ({
    to,
    onClick,
    icon,
    title,
    subtitle,
    status,
    addon,
    chips = [],
    meta = [],
    muted = false,
    className,
}: {
    to?: string;
    onClick?: () => void;
    icon: React.ReactNode;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    status?: React.ReactNode;
    addon?: React.ReactNode;
    chips?: string[];
    meta?: AssistantCardMetaLine[];
    muted?: boolean;
    className?: string;
}) => {
    const isInteractive = !!to || !!onClick;
    // hover, and while the header menu is open: the weak-hover surface and the stronger hover border (hub card shell)
    const cardClassName = classnames(
        'group flex flex-col gap-2.5 rounded-lg border border-solid border-line-weak bg-surface-weak p-5 transition-colors',
        isInteractive && 'hover:border-line-hover hover:bg-surface-weak-hover',
        '[&:has([data-state=open])]:border-line-hover [&:has([data-state=open])]:bg-surface-weak-hover',
        isInteractive &&
            'focus-visible:border-line-focus focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-line-focus',
        muted && 'opacity-60',
        className
    );
    const content = (
        <>
            <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2.5">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-surface-agent-weak text-content-agent-weak">
                        {icon}
                    </span>
                    <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium leading-5 text-content-strong">{title}</span>
                        {(status || subtitle) && (
                            <span className="flex min-w-0 items-center gap-1.5 text-xs leading-5 text-content-medium">
                                {status}
                                {status && subtitle && <span aria-hidden="true">·</span>}
                                {subtitle && <span className="truncate">{subtitle}</span>}
                            </span>
                        )}
                    </div>
                </div>
                {addon}
            </div>

            {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {chips.map((chip) => (
                        <span
                            key={chip}
                            className="flex h-6 items-center rounded-sm border border-line-weak bg-surface-medium px-2 text-xs font-medium text-content-strong"
                        >
                            {chip}
                        </span>
                    ))}
                </div>
            )}

            {meta.length > 0 && (
                <div className="mt-auto flex flex-col gap-2.5 pt-1 text-xs leading-5 text-content-medium">
                    {meta.map((line, index) => (
                        <span key={index} className="flex min-w-0 items-center gap-2">
                            <span className="flex w-4 flex-none justify-center">{line.icon}</span>
                            {line.label && <span className="min-w-0 flex-1 truncate">{line.label}</span>}
                            {line.trailing && (
                                <span className="flex flex-none items-center gap-2">{line.trailing}</span>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </>
    );
    if (to)
        return (
            <Link to={to} className={cardClassName}>
                {content}
            </Link>
        );
    if (!onClick) return <div className={cardClassName}>{content}</div>;
    // a div rather than a <button>: the header addon may hold its own buttons (e.g. a menu trigger)
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                onClick();
            }}
            className={classnames(cardClassName, 'cursor-pointer')}
        >
            {content}
        </div>
    );
};

export default AssistantCardShell;
